import React, { useEffect, useState, useCallback, useMemo } from 'react';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { Box, Button, TextField, Typography, Snackbar, Alert } from '@mui/material';
import Swal from 'sweetalert2';
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/authContext';
import { useSite } from "../../context/siteContext";
import { contactCCR } from '../../services/contactCCRFormService';
import { getMachineByNumberOrId } from "../../services/myMachineService";
import { useOptions } from "../../context/optionContext";
import {
	parseEmergencyPhones,
	resolveEmergencyNumberForRegion,
	resolveSupportSettings,
	SUPPORT_DEFAULT_REGION_PHONES,
} from "../../utilities/supportSettings";
import PhoneInput from "../PhoneInput/PhoneInput";
import { resolvePhoneState } from "../../utilities/phoneUtils";

const EMPTY_FORM = {
	name: '',
	email: '',
	phone: '',
	machine_number: '',
	subject: '',
	message: '',
};

const ContactCCRForm = ({ open, setOpen, machine, prefill }) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const { selectedSite, selectedSiteData } = useSite();
	const { optionKeys } = useOptions();
	const location = useLocation();

	const [errors, setErrors] = useState({});
	const [formData, setFormData] = useState(EMPTY_FORM);
	const [routeMachine, setRouteMachine] = useState(null);

	const [successOpen, setSuccessOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const isNameLocked = !!user?.user_display_name;
	const isEmailLocked = !!user?.user_email;
	const isPhoneLocked = !!user?.user_phone;
	const emergencyPhone = useMemo(() => {
		const supportSettings = resolveSupportSettings(optionKeys || {});
		if (!supportSettings.contactCcrShowEmergencyMessage) return null;
		const configured = parseEmergencyPhones(optionKeys?.customerLocationEmergencyPhones, SUPPORT_DEFAULT_REGION_PHONES);
		return resolveEmergencyNumberForRegion({
			source: supportSettings.contactCcrEmergencyNumberSource,
			manualNumber: supportSettings.contactCcrEmergencyNumberManual,
			userRegion: user?.region,
			phones: configured,
		});
	}, [user?.region, optionKeys]);

	const buildInitialForm = useCallback(() => {
		const resolvedMachine = machine || routeMachine;
		const phoneState = resolvePhoneState({
			phoneValue: user?.user_phone || "",
			countryLabel: user?.country || user?.user_meta?.country || "",
			phoneCountryIso2: user?.phone_country_iso2 || user?.user_meta?.phone_country_iso2 || "",
			phoneCountryCode: user?.phone_country_code || user?.user_meta?.phone_country_code || "",
		});
		const base = {
			name: user?.user_display_name || '',
			phone: phoneState.phoneValue,
			phone_country_iso2: phoneState.meta.phone_country_iso2,
			phone_country_code: phoneState.meta.phone_country_code,
			email: user?.user_email || '',
		};

		const machineExtras = resolvedMachine
			? {
				subject: `${t('UI_TEXT.PARTS_FOR')} ${resolvedMachine?.machine_name} - ${resolvedMachine?.machine_number} ${t('UI_TEXT.ARE_NOT_AVAILABLE')}`,
				machine_number: resolvedMachine?.machine_number || '',
				message: `${t('UI_TEXT.I_WOULD_LIKE_TO_ORDER_A_PART_FOR_MY_MACHINE')} - ${resolvedMachine?.machine_name} - ${resolvedMachine?.machine_number} ${t('UI_TEXT.BUT_THE_PARTS_ARE_NOT_AVAILABLE_PLEASE_SEND_THE_PARTS_LIST_IF_POSSIBLE')}`,
			}
			: {};

		const override = prefill && typeof prefill === "object" ? prefill : {};

		return {
			...EMPTY_FORM,
			...base,
			...machineExtras,
			...override,
			site_number: selectedSite,
			site_id: selectedSiteData?.site_id || null,
			cus_id: selectedSiteData?.cus_id || null,
		};
	}, [user, machine, routeMachine, prefill, selectedSite, selectedSiteData?.site_id, selectedSiteData?.cus_id]);

	useEffect(() => {
		if (!open) {
			setRouteMachine(null);
			return;
		}
		if (machine) return;
		if (!location.pathname.includes("/machines")) {
			setRouteMachine(null);
			return;
		}
		if (!selectedSiteData?.site_id) return;

		const segments = location.pathname.split("/").filter(Boolean);
		const machineToken = segments[1];
		if (!machineToken) return;

		const machineId = Number(machineToken);
		if (!Number.isFinite(machineId)) return;

		const fetchMachine = async () => {
			try {
				const response = await getMachineByNumberOrId({
					machine_id: machineId,
					site_id: selectedSiteData.site_id,
				});
				if (response?.status && Array.isArray(response?.data) && response.data.length > 0) {
					setRouteMachine(response.data[0]);
				}
			} catch (err) {
				console.warn("Failed to load machine for CCR form:", err?.message);
			}
		};

		fetchMachine();
	}, [open, machine, location.pathname, selectedSiteData?.site_id]);

	// Reset form + errors every time the modal opens (and also when it closes)
	useEffect(() => {
		if (open) {
			setErrors({});
			setFormData(buildInitialForm());
		} else {
			// clear out any leftover validation state so it doesn't flash next open
			setErrors({});
			setFormData(EMPTY_FORM);
		}
	}, [open, buildInitialForm]);

	const clearFieldError = (field) => {
		setErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const validate = () => {
		const e = {};

		if (!formData.name?.trim()) e.name = t('UI_TEXT.NAME_REQUIRED');

		if (!formData.email?.trim()) {
			e.email = t('ALERT_MESSAGES.EMAIL_IS_REQUIRED');
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			e.email = t('ALERT_MESSAGES.INVALID_EMAIL_FORMAT');
		}

		if (!formData.phone?.trim()) e.phone = t('UI_TEXT.PHONE_IS_REQUIRED');

		// ✅ Missing before: machine_number validation
		if (!formData.machine_number?.trim()) e.machine_number = t('UI_TEXT.MACHINE_NUMBER_IS_REQUIRED');

		if (!formData.subject?.trim()) e.subject = t('UI_TEXT.SUBJECT_IS_REQUIRED');
		if (!formData.message?.trim()) e.message = t('UI_TEXT.MESSAGE_IS_REQUIRED');

		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const onClose = () => setOpen(false);

	const onSubmit = async () => {
		if (!validate()) return;

		setSubmitting(true);
		try {
			await contactCCR(formData);

			// close modal first so swal feels like a post-submit confirmation
			setOpen(false);

			await Swal.fire({
				icon: 'success',
				title: t('UI_TEXT.SUBMITTED_1'),
				text: t('UI_TEXT.YOUR_MESSAGE_HAS_BEEN_SENT_SUCCESSFULLY_THE_CCR_TEAM_WILL_REACH_OUT_TO_YOU_SOON'),
				confirmButtonText: t('GENERAL.OK'),
				allowOutsideClick: false,
			});

			// optional: reset (useEffect already resets on close, but this is safe)
			setErrors({});
			setFormData(EMPTY_FORM);
		} catch (err) {
			console.error(err);

			await Swal.fire({
				icon: 'error',
				title: t('UI_TEXT.SUBMISSION_FAILED'),
				text: t('UI_TEXT.SOMETHING_WENT_WRONG_WHILE_SENDING_YOUR_MESSAGE_PLEASE_TRY_AGAIN'),
				confirmButtonText: t('GENERAL.OK'),
			});
		} finally {
			setSubmitting(false);
		}
	};


	return (
		<>
			<ModalWrapper
				open={open}
				setOpen={setOpen}
				title={t('LABELS.CONTACT_CCR')}
				subtitle={t('UI_TEXT.PLEASE_FILL_OUT_THE_FORM_BELOW_AND_THE_CCR_TEAM_WILL_REACH_OUT_TO_YOU')}
				content={
					<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
						<TextField
							label={t('TABLE_HEADERS.NAME')}
							fullWidth
							size="small"
							margin="normal"
							required
							disabled={isNameLocked}
							value={formData.name}
							error={Boolean(errors.name)}
							helperText={errors.name || ''}
							onChange={(e) => {
								clearFieldError('name');
								setFormData((p) => ({ ...p, name: e.target.value }));
							}}
						/>

						<TextField
							label={t('TABLE_HEADERS.EMAIL')}
							fullWidth
							size="small"
							margin="normal"
							required
							disabled={isEmailLocked}
							value={formData.email}
							error={Boolean(errors.email)}
							helperText={errors.email || ''}
							onChange={(e) => {
								clearFieldError('email');
								setFormData((p) => ({ ...p, email: e.target.value }));
							}}
						/>

						<PhoneInput
							value={formData.phone}
							onChange={(value) => {
								clearFieldError('phone');
								setFormData((p) => ({ ...p, phone: value }));
							}}
							countryIso2={formData.phone_country_iso2}
							countryCode={formData.phone_country_code}
							allowCountryChange={false}
							disabled={isPhoneLocked}
							label={t('LABELS.PHONE')}
							size="small"
							margin="normal"
							error={Boolean(errors.phone)}
							helperText={errors.phone || ''}
							onCountryChange={() => {}}
						/>

						<TextField
							label={t('TABLE_HEADERS.MACHINE_NUMBER')}
							fullWidth
							size="small"
							margin="normal"
							required
							value={formData.machine_number}
							error={Boolean(errors.machine_number)}
							helperText={errors.machine_number || ''}
							onChange={(e) => {
								clearFieldError('machine_number');
								setFormData((p) => ({ ...p, machine_number: e.target.value }));
							}}
						/>

						<TextField
							label={t('TABLE_HEADERS.SUBJECT')}
							fullWidth
							size="small"
							margin="normal"
							required
							value={formData.subject}
							error={Boolean(errors.subject)}
							helperText={errors.subject || ''}
							onChange={(e) => {
								clearFieldError('subject');
								setFormData((p) => ({ ...p, subject: e.target.value }));
							}}
						/>

						<TextField
							label={t('TABLE_HEADERS.MESSAGE')}
							fullWidth
							multiline
							rows={4}
							size="small"
							margin="normal"
							required
							value={formData.message}
							error={Boolean(errors.message)}
							helperText={errors.message || ''}
							onChange={(e) => {
								clearFieldError('message');
								setFormData((p) => ({ ...p, message: e.target.value }));
							}}
						/>

						{emergencyPhone && (
							<Typography variant="body1" fontSize="11px" sx={{ color: "error.main" }}>
								{t('UI_TEXT.IF_THIS_IS_AN_EMERGENCY_PLEASE_CALL_THE_EMERGENCY_NUMBER')}{" "}
								<a href={`tel:${emergencyPhone}`} style={{ color: "inherit", textDecoration: "underline", fontWeight: 600 }}>
									{emergencyPhone}
								</a>
							</Typography>
						)}
					</Box>
				}
				actions={
					<>
						<Button onClick={onClose} disabled={submitting}>
							{t('BUTTON.CANCEL')}
						</Button>
						<Button color="primary" variant="contained" onClick={onSubmit} disabled={submitting}>
							{submitting ? 'Submitting…' : t('BUTTON.SUBMIT')}
						</Button>
					</>
				}
			/>

			<Snackbar
				open={successOpen}
				autoHideDuration={4000}
				onClose={() => setSuccessOpen(false)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled">
					{t('UI_TEXT.YOUR_MESSAGE_HAS_BEEN_SENT_SUCCESSFULLY_THE_CCR_TEAM_WILL_REACH_OUT_TO_YOU_SOON')}
				</Alert>
			</Snackbar>
		</>
	);
};

export default ContactCCRForm;
